// Extra color math for the a11y / palette / picker cluster.
// Pure functions, no dependencies. Reuses parseHex/rgbToHex/rgbToHsl from browser-color-tools.

import { parseHex, rgbToHex, rgbToHsl, type Rgb } from './browser-color-tools';

export type { Rgb };
export { parseHex, rgbToHex, rgbToHsl };

export type Hsl = { h: number; s: number; l: number };

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

// Convert an HSL color (h 0-360, s/l 0-100) back to RGB (0-255).
export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const hn = ((h % 360) + 360) % 360;
  const sn = Math.max(0, Math.min(100, s)) / 100;
  const ln = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = ln - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hn < 60) {
    rp = c;
    gp = x;
  } else if (hn < 120) {
    rp = x;
    gp = c;
  } else if (hn < 180) {
    gp = c;
    bp = x;
  } else if (hn < 240) {
    gp = x;
    bp = c;
  } else if (hn < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  return {
    r: clampChannel((rp + m) * 255),
    g: clampChannel((gp + m) * 255),
    b: clampChannel((bp + m) * 255),
  };
}

// Relative luminance per WCAG 2.x definition.
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// WCAG contrast ratio between two colors (1 to 21).
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export type WcagResults = {
  ratio: number;
  ratioLabel: string;
  aaNormal: boolean;
  aaLarge: boolean;
  aaaNormal: boolean;
  aaaLarge: boolean;
};

export function evaluateWcag(foreground: Rgb, background: Rgb): WcagResults {
  const ratio = contrastRatio(foreground, background);
  return {
    ratio,
    ratioLabel: `${ratio.toFixed(2)}:1`,
    aaNormal: ratio >= 4.5,
    aaLarge: ratio >= 3,
    aaaNormal: ratio >= 7,
    aaaLarge: ratio >= 4.5,
  };
}

export function formatRgbString({ r, g, b }: Rgb): string {
  return `rgb(${r}, ${g}, ${b})`;
}

export function formatHslString(hsl: Hsl): string {
  return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
}

export type PaletteKind =
  | 'complementary'
  | 'analogous'
  | 'triadic'
  | 'monochromatic'
  | 'shades-tints';

export type PaletteGroup = { kind: PaletteKind; label: string; colors: string[] };

function rotate(hsl: Hsl, degrees: number): string {
  return rgbToHex(hslToRgb({ h: hsl.h + degrees, s: hsl.s, l: hsl.l }));
}

function withLightness(hsl: Hsl, l: number): string {
  return rgbToHex(hslToRgb({ h: hsl.h, s: hsl.s, l: Math.max(0, Math.min(100, l)) }));
}

// Build the five algorithmic palettes from a base color.
export function buildPalettes(base: Rgb): PaletteGroup[] {
  const hsl = rgbToHsl(base);
  const baseHex = rgbToHex(base);

  const complementary = [baseHex, rotate(hsl, 180)];

  const analogous = [rotate(hsl, -30), baseHex, rotate(hsl, 30)];

  const triadic = [baseHex, rotate(hsl, 120), rotate(hsl, 240)];

  const monochromatic = [20, 35, 50, 65, 80].map((l) => withLightness(hsl, l));

  const shadesTints = [10, 25, 40, hsl.l, 60, 75, 90]
    .filter((value, index, arr) => arr.indexOf(value) === index)
    .sort((a, b) => a - b)
    .map((l) => withLightness(hsl, l));

  return [
    { kind: 'complementary', label: 'Complementary', colors: complementary },
    { kind: 'analogous', label: 'Analogous', colors: analogous },
    { kind: 'triadic', label: 'Triadic', colors: triadic },
    { kind: 'monochromatic', label: 'Monochromatic', colors: monochromatic },
    { kind: 'shades-tints', label: 'Shades & tints', colors: shadesTints },
  ];
}
